import type { Firestore } from 'firebase-admin/firestore'
import { createLogger } from '../../common/utils/logger.js'
import { calculateDistance, geohash } from '@my-roadtrip/utils'
import type { AIPOISuggestion } from '@my-roadtrip/types'

const logger = createLogger('POIDatabaseService')

/**
 * In-memory cache for geohash queries
 * Key: "geohash{precision}:{hash}" e.g. "geohash5:u09tu"
 * Value: { pois: POI[], timestamp: number }
 * 
 * This dramatically speeds up:
 * 1. Zoom in/out (child/parent geohashes overlap)
 * 2. Small pans (neighboring geohashes often cached)
 * 3. Return visits to same area
 */
interface GeohashCacheEntry {
  pois: any[]
  timestamp: number
}

const geohashCache = new Map<string, GeohashCacheEntry>()
const GEOHASH_CACHE_TTL = 10 * 60 * 1000  // 10 minutes (was 5)
const GEOHASH_CACHE_MAX_SIZE = 1000       // Max cached geohashes (was 500)

function getFromGeohashCache(precision: number, hash: string): any[] | null {
  const key = `geohash${precision}:${hash}`
  const entry = geohashCache.get(key)
  if (!entry) return null
  
  // Check TTL
  if (Date.now() - entry.timestamp > GEOHASH_CACHE_TTL) {
    geohashCache.delete(key)
    return null
  }
  
  return entry.pois
}

function setGeohashCache(precision: number, hash: string, pois: any[]): void {
  // Evict old entries if cache is full
  if (geohashCache.size >= GEOHASH_CACHE_MAX_SIZE) {
    const oldest = [...geohashCache.entries()]
      .sort((a, b) => a[1].timestamp - b[1].timestamp)
      .slice(0, 100)
    oldest.forEach(([key]) => geohashCache.delete(key))
  }
  
  const key = `geohash${precision}:${hash}`
  geohashCache.set(key, { pois, timestamp: Date.now() })
}

/**
 * POI Database Service
 * 
 * Persistent POI database enriched by all users' searches.
 * Acts as a "collective knowledge base" that grows with usage.
 * 
 * Strategy:
 * 1. Every AI-generated POI is saved to permanent database
 * 2. Future searches first query this database (fast, free)
 * 3. Only generate new POIs if database doesn't have enough results
 * 4. Over time, database becomes comprehensive and AI calls drop to near-zero
 */
export class POIDatabaseService {
  constructor(private readonly db: Firestore) { }

  /**
   * Search POIs from permanent database
   * Uses adaptive multi-precision geohash for efficient geographic queries
   * 
   * Strategy:
   * - Small radius (<10km): Use precision 6 (±600m) - Dense urban areas
   * - Medium radius (10-30km): Use precision 5 (±5km) - Suburban areas  
   * - Large radius (>30km): Use precision 4 (±20km) - Rural areas
   * - Fallback cascade: If not enough results, try lower precision
   * 
   * @returns POIs from database matching criteria
   */
  async searchNearby(
    location: { lat: number; lng: number },
    categories: string[],
    radiusKm: number,
    options?: {
      minConfidence?: number
      limit?: number
      excludeIds?: string[]
    }
  ): Promise<AIPOISuggestion[]> {
    const startTime = Date.now()

    try {
      // Adaptive precision based on search radius
      const precision = this.getOptimalPrecision(radiusKm)
      const geohashField = `geohash${precision}` as 'geohash4' | 'geohash5' | 'geohash6'

      // Generate geohash neighbors for area coverage
      const centerHash = geohash(location.lat, location.lng, precision)
      const neighbors = this.getGeohashNeighbors(centerHash)
      const searchHashes = [centerHash, ...neighbors] // 9 cells total

      logger.debug('Searching POI database', {
        location,
        categories,
        radiusKm,
        precision,
        searchHashes: searchHashes.length,
        centerHash
      })

      // Query Firestore with geohash + categories
      const queries = searchHashes.map(hash =>
        this.db
          .collection('poi_database')
          .where(geohashField, '==', hash)
          .where('categories', 'array-contains-any', categories.slice(0, 10)) // Firestore limit
          .limit(20)
          .get()
      )

      let snapshots = await Promise.all(queries)

      // Fallback cascade: If not enough results, try lower precision
      const initialResults = this.countUniqueResults(snapshots)
      const targetResults = options?.limit || 50

      if (initialResults < targetResults && precision > 4) {
        logger.debug('Insufficient results, trying lower precision fallback', {
          found: initialResults,
          target: targetResults,
          currentPrecision: precision,
          nextPrecision: precision - 1
        })

        // Retry with lower precision (wider area)
        const fallbackPrecision = (precision - 1) as 4 | 5 | 6
        const fallbackHash = geohash(location.lat, location.lng, fallbackPrecision)
        const fallbackNeighbors = this.getGeohashNeighbors(fallbackHash)
        const fallbackHashes = [fallbackHash, ...fallbackNeighbors]
        const fallbackField = `geohash${fallbackPrecision}` as 'geohash4' | 'geohash5' | 'geohash6'

        const fallbackQueries = fallbackHashes.map(hash =>
          this.db
            .collection('poi_database')
            .where(fallbackField, '==', hash)
            .where('categories', 'array-contains-any', categories.slice(0, 10))
            .limit(20)
            .get()
        )

        const fallbackSnapshots = await Promise.all(fallbackQueries)
        snapshots = [...snapshots, ...fallbackSnapshots]

        logger.debug('Fallback search completed', {
          additionalResults: this.countUniqueResults(fallbackSnapshots),
          totalResults: this.countUniqueResults(snapshots),
          fallbackPrecision
        })
      }

      // Collect and deduplicate POIs
      const poisMap = new Map<string, AIPOISuggestion>()

      for (const snapshot of snapshots) {
        for (const doc of snapshot.docs) {
          const data = doc.data()

          // Calculate distance
          const distanceKm = calculateDistance(location, { lat: data.lat, lng: data.lng }) / 1000

          // Filter by radius
          if (distanceKm > radiusKm) continue

          // Filter by confidence
          if (options?.minConfidence && (data.confidence || 0) < options.minConfidence) continue

          // Filter by excluded IDs
          if (options?.excludeIds?.includes(doc.id)) continue

          // Build POI object
          const poi: AIPOISuggestion = {
            id: doc.id,
            name: data.name,
            lat: data.lat,
            lng: data.lng,
            category: data.category,
            description: data.description || '',
            rating: data.rating,
            uniqueness: data.uniqueness || 50,
            tags: data.tags || [],
            confidence: data.confidence || 70,
            distanceKm,
            coverImage: data.coverImage,
            names: data.names || undefined, // Multilingual names from database
            verification: {
              source: 'database',
              matched: true,
              confidence: data.confidence || 70
            }
          }

          poisMap.set(doc.id, poi)
        }
      }

      // Sort by distance
      const pois = Array.from(poisMap.values())
        .sort((a, b) => (a.distanceKm || 0) - (b.distanceKm || 0))
        .slice(0, options?.limit || 50)

      const elapsedMs = Date.now() - startTime

      logger.info('POI database search complete', {
        found: pois.length,
        searchHashes: searchHashes.length,
        elapsedMs,
        centerHash
      })

      return pois
    } catch (error) {
      logger.error('POI database search failed', { error, location, categories })
      return []
    }
  }

  /**
   * Save POI to permanent database
   * Called after every successful AI generation
   */
  async savePOI(poi: {
    name: string
    lat: number
    lng: number
    category: string
    description?: string
    rating?: number
    uniqueness?: number
    tags?: string[]
    confidence?: number
    coverImage?: string
    verified?: boolean
    source: string
    metadata?: any
  }): Promise<string> {
    try {
      // Generate geohash for indexing (multiple precisions for adaptive search)
      const hash4 = geohash(poi.lat, poi.lng, 4)  // Rural search (±39km)
      const hash5 = geohash(poi.lat, poi.lng, 5)  // Suburban search (±4.9km)
      const hash6 = geohash(poi.lat, poi.lng, 6)  // Urban search (±0.61km)
      const hash7 = geohash(poi.lat, poi.lng, 7)
      const hash8 = geohash(poi.lat, poi.lng, 8)

      // Create document ID from name + location (for deduplication)
      const docId = this.generatePOIId(poi.name, poi.lat, poi.lng)

      // Check if POI already exists
      const existingDoc = await this.db.collection('poi_database').doc(docId).get()

      if (existingDoc.exists) {
        // Update existing POI (merge data, increase confidence)
        const existing = existingDoc.data()!
        const updatedConfidence = Math.min(100, (existing.confidence || 50) + 5) // Increase confidence
        const updatedSeenCount = (existing.seenCount || 1) + 1

        await this.db.collection('poi_database').doc(docId).update({
          confidence: updatedConfidence,
          seenCount: updatedSeenCount,
          lastSeenAt: new Date(),
          // Merge tags
          tags: Array.from(new Set([...(existing.tags || []), ...(poi.tags || [])])),
          // Keep best data
          rating: poi.rating || existing.rating,
          coverImage: poi.coverImage || existing.coverImage,
          verified: poi.verified || existing.verified,
          updatedAt: new Date()
        })

        logger.debug('POI updated in database', {
          id: docId,
          name: poi.name,
          newConfidence: updatedConfidence,
          seenCount: updatedSeenCount
        })

        return docId
      }

      // Create new POI
      const poiDoc = {
        name: poi.name,
        lat: poi.lat,
        lng: poi.lng,
        category: poi.category,
        categories: [poi.category, ...(poi.tags || [])], // For array-contains-any queries
        description: poi.description || '',
        rating: poi.rating,
        uniqueness: poi.uniqueness || 50,
        tags: poi.tags || [],
        confidence: poi.confidence || 70,
        coverImage: poi.coverImage,
        verified: poi.verified || false,
        source: poi.source,

        // Geographic indexing (multi-precision for adaptive search)
        geohash4: hash4,
        geohash5: hash5,
        geohash6: hash6,
        geohash7: hash7,
        geohash8: hash8,

        // Metadata
        seenCount: 1,
        firstSeenAt: new Date(),
        lastSeenAt: new Date(),
        createdAt: new Date(),
        updatedAt: new Date(),
        metadata: poi.metadata || {}
      }

      await this.db.collection('poi_database').doc(docId).set(poiDoc)

      logger.info('POI saved to database', {
        id: docId,
        name: poi.name,
        category: poi.category,
        geohash6: hash6
      })

      return docId
    } catch (error) {
      logger.error('Failed to save POI to database', { error, poi: poi.name })
      throw error
    }
  }

  /**
   * Batch save multiple POIs
   */
  async savePOIs(pois: Array<Parameters<typeof this.savePOI>[0]>): Promise<string[]> {
    const startTime = Date.now()

    try {
      // Use Promise.all for parallel saves (but respect Firestore limits)
      const batchSize = 10
      const ids: string[] = []

      for (let i = 0; i < pois.length; i += batchSize) {
        const batch = pois.slice(i, i + batchSize)
        const batchIds = await Promise.all(
          batch.map(poi => this.savePOI(poi))
        )
        ids.push(...batchIds)
      }

      const elapsedMs = Date.now() - startTime
      logger.info('Batch POI save complete', {
        count: pois.length,
        elapsedMs,
        avgMs: Math.round(elapsedMs / pois.length)
      })

      return ids
    } catch (error) {
      logger.error('Batch POI save failed', { error, count: pois.length })
      throw error
    }
  }

  /**
   * Get POI statistics
   */
  async getStats(): Promise<{
    totalPOIs: number
    byCategory: Record<string, number>
    avgConfidence: number
    topLocations: Array<{ geohash: string; count: number }>
  }> {
    try {
      // This would be expensive in production - use aggregation or cached stats
      const snapshot = await this.db
        .collection('poi_database')
        .limit(10000)
        .get()

      const byCategory: Record<string, number> = {}
      let totalConfidence = 0

      for (const doc of snapshot.docs) {
        const data = doc.data()
        byCategory[data.category] = (byCategory[data.category] || 0) + 1
        totalConfidence += data.confidence || 0
      }

      return {
        totalPOIs: snapshot.size,
        byCategory,
        avgConfidence: snapshot.size > 0 ? totalConfidence / snapshot.size : 0,
        topLocations: [] // Would need aggregation
      }
    } catch (error) {
      logger.error('Failed to get POI stats', { error })
      return {
        totalPOIs: 0,
        byCategory: {},
        avgConfidence: 0,
        topLocations: []
      }
    }
  }

  /**
   * Generate consistent POI ID for deduplication
   */
  private generatePOIId(name: string, lat: number, lng: number): string {
    // Normalize name
    const normalizedName = name
      .toLowerCase()
      .normalize('NFD')
      .replaceAll(/[\u0300-\u036f]/g, '') // Remove accents
      .replaceAll(/[^a-z0-9]/g, '-')
      .replaceAll(/-+/g, '-')
      .substring(0, 50)

    // Round coordinates to ~11m precision (5 decimals)
    const roundedLat = Math.round(lat * 100000) / 100000
    const roundedLng = Math.round(lng * 100000) / 100000

    return `${normalizedName}-${roundedLat}-${roundedLng}`
  }

  /**
   * Determine optimal geohash precision based on search radius
   * 
   * Precision levels:
   * - 6: ±0.61 km (urban, dense areas)
   * - 5: ±4.9 km (suburban, medium density)
   * - 4: ±39 km (rural, sparse areas)
   */
  private getOptimalPrecision(radiusKm: number): 4 | 5 | 6 {
    if (radiusKm <= 10) return 6      // Dense urban search
    if (radiusKm <= 30) return 5      // Suburban search
    return 4                           // Rural/regional search
  }

  /**
   * Count unique POIs across snapshots (deduplicate by doc ID)
   */
  private countUniqueResults(snapshots: FirebaseFirestore.QuerySnapshot[]): number {
    const uniqueIds = new Set<string>()
    for (const snapshot of snapshots) {
      for (const doc of snapshot.docs) {
        uniqueIds.add(doc.id)
      }
    }
    return uniqueIds.size
  }

  /**
   * Get 8 neighboring geohash cells
   * Ensures complete coverage of search area
   */
  private getGeohashNeighbors(hash: string): string[] {
    if (!hash) return []

    const base32 = '0123456789bcdefghjkmnpqrstuvwxyz'
    const neighborsMap = {
      n: ['p0r21436x8zb9dcf5h7kjnmqesgutwvy', 'bc01fg45238967deuvhjyznpkmstqrwx'],
      s: ['bc01fg45238967deuvhjyznpkmstqrwx', 'p0r21436x8zb9dcf5h7kjnmqesgutwvy'],
      e: ['bc01rdmh2a3456789defghjkmnpqrstuwvyz', '238967debc01fg45kmstqrwxuvhjyznp'],
      w: ['238967debc01fg45kmstqrwxuvhjyznp', 'bc01rdmh2a3456789defghjkmnpqrstuwvyz']
    }
    const bordersMap = {
      n: ['prxz', 'bcfguvyz'],
      s: ['028b', '0145hjnp'],
      e: ['bcfguvyz', 'prxz'],
      w: ['0145hjnp', '028b']
    }

    const calculateNeighbor = (cell: string, direction: keyof typeof neighborsMap): string => {
      const lastChar = cell.slice(-1)
      const parent = cell.slice(0, -1)
      const type = cell.length % 2 // 0 = even, 1 = odd

      const border = bordersMap[direction][type]
      const neighbors = neighborsMap[direction][type]

      const neighborParent =
        parent && border.includes(lastChar)
          ? calculateNeighbor(parent, direction)
          : parent

      const neighborIndex = neighbors.indexOf(lastChar)
      const neighborChar = base32[neighborIndex] ?? lastChar

      return `${neighborParent}${neighborChar}`
    }

    const north = calculateNeighbor(hash, 'n')
    const south = calculateNeighbor(hash, 's')
    const east = calculateNeighbor(hash, 'e')
    const west = calculateNeighbor(hash, 'w')

    return [
      north,
      south,
      east,
      west,
      calculateNeighbor(north, 'e'),
      calculateNeighbor(north, 'w'),
      calculateNeighbor(south, 'e'),
      calculateNeighbor(south, 'w')
    ]
  }

  /**
   * Search POIs by name (prefix search)
   * Used for global search bar
   */
  async searchByName(query: string, limit = 10): Promise<AIPOISuggestion[]> {
    if (!query || query.length < 2) return []

    // Normalize query for search
    // Note: This assumes we have a 'name' field we can query. 
    // Ideally we should have a normalized 'searchName' field in lowercase.
    // For now, we'll try a simple range query on 'name' which is case-sensitive.
    // TODO: Add 'searchName' field to all POIs for better search

    // Capitalize first letter as a best-effort for case-sensitivity
    const searchStr = query.charAt(0).toUpperCase() + query.slice(1)
    const endStr = searchStr + '\uf8ff'

    try {
      const snapshot = await this.db
        .collection('poi_database')
        .where('name', '>=', searchStr)
        .where('name', '<=', endStr)
        .limit(limit)
        .get()

      return snapshot.docs.map(doc => {
        const data = doc.data()
        return {
          id: doc.id,
          name: data.name,
          lat: data.lat,
          lng: data.lng,
          category: data.category,
          description: data.description || '',
          rating: data.rating,
          uniqueness: data.uniqueness || 50,
          tags: data.tags || [],
          confidence: data.confidence || 70,
          coverImage: data.coverImage,
          verification: {
            source: 'database',
            matched: true,
            confidence: data.confidence || 70
          }
        }
      })
    } catch (error) {
      logger.error('POI name search failed', { error, query })
      return []
    }
  }

  /**
   * Search POIs within a map viewport (bounding box)
   * Optimized for map display with 50k+ POIs
   * 
   * Strategy:
   * - Uses geohash-based spatial queries for efficiency
   * - Limits results based on zoom level (more POIs at higher zoom)
   * - Returns only essential fields for markers
   * - Prioritizes high-confidence/popular POIs when limiting
   * 
   * @param bounds - Viewport bounding box {north, south, east, west}
   * @param options - Filtering and pagination options
   */
  async searchInViewport(
    bounds: {
      north: number
      south: number
      east: number
      west: number
    },
    options?: {
      categories?: string[]
      zoom?: number
      limit?: number
      minConfidence?: number
      includeDetails?: boolean
    }
  ): Promise<{
    pois: Array<{
      id: string
      lat: number
      lng: number
      category: string
      name?: string
      rating?: number
      confidence?: number
      truncated?: boolean
    }>
    total: number
    truncated: boolean
    clusterStats?: {
      clusterCount: number
      avgPerCluster: number
      maxPerCluster: number
      truncatedClusters: number
    }
    lightweight: boolean
  }> {
    const startTime = Date.now()
    const zoom = options?.zoom ?? 10
    const categories = options?.categories ?? []
    
    // Calculate viewport size
    const latRange = bounds.north - bounds.south
    const lngRange = bounds.east - bounds.west
    const approxViewportKm = Math.max(latRange, lngRange) * 111
    
    // STRATEGY:
    // - Small viewport (<20km): Load ALL POIs (no limit) - user zoomed in, wants detail
    // - Medium viewport (20-100km): Limit to 2000 for performance
    // - Large viewport (>100km): Limit to 1500 for performance, clusters handle the rest
    const isSmallViewport = approxViewportKm < 20
    const isMediumViewport = approxViewportKm >= 20 && approxViewportKm < 100
    
    let maxLimit: number
    if (isSmallViewport) {
      maxLimit = 5000  // Effectively unlimited for small areas
    } else if (isMediumViewport) {
      maxLimit = 2000
    } else {
      maxLimit = 1500
    }
    
    const limit = Math.min(maxLimit, options?.limit ?? maxLimit)
    const lightweight = options?.includeDetails === false || zoom < 13
    
    if (zoom < 4) {
      return { pois: [], total: 0, truncated: false, lightweight: true }
    }

    logger.info('🚀 POI search START', {
      zoom,
      viewportKm: approxViewportKm.toFixed(1),
      limit,
      isSmallViewport,
      strategy: isSmallViewport ? 'LOAD ALL' : 'SAMPLED'
    })

    try {
      // Choose geohash precision based on viewport size
      let queryPrecision: 4 | 5 | 6
      if (approxViewportKm > 500) queryPrecision = 4
      else if (approxViewportKm > 100) queryPrecision = 5
      else queryPrecision = 6
      
      const geohashField = `geohash${queryPrecision}` as const
      
      // Get geohashes covering this viewport (spiral order from center)
      const allGeohashes = this.getGeohashesForBounds(bounds, queryPrecision)
      
      // For small viewports: query ALL geohashes to get ALL POIs
      // For large viewports: sample geohashes for uniform distribution
      const queryLimit = isSmallViewport ? 100 : 10  // More geohashes for small viewports
      const geohashesToQuery = allGeohashes.slice(0, queryLimit)
      
      logger.debug('📍 Geohashes', {
        total: allGeohashes.length,
        querying: geohashesToQuery.length,
        precision: queryPrecision
      })
      
      // Query Firestore - chunk geohashes due to 'in' limit of 10
      const allPois: any[] = []
      const seenIds = new Set<string>()
      
      const chunks = this.chunkArray(geohashesToQuery, 10)
      
      for (const chunk of chunks) {
        if (allPois.length >= limit) break
        
        let query = this.db
          .collection('poi_database')
          .where(geohashField, 'in', chunk)
          .orderBy('confidence', 'desc')
          .limit(isSmallViewport ? 2000 : 500)  // Higher limit per chunk for small viewports
        
        if (lightweight) {
          query = query.select('lat', 'lng', 'category', 'confidence', 'name', 'geohash4', 'geohash5', 'geohash6')
        }
        
        const snapshot = await query.get()
        
        for (const doc of snapshot.docs) {
          if (allPois.length >= limit) break
          
          const data = doc.data()
          if (seenIds.has(doc.id)) continue
          
          // Filter by bounds
          if (data.lat < bounds.south || data.lat > bounds.north ||
              data.lng < bounds.west || data.lng > bounds.east) {
            continue
          }
          
          // Filter by category if specified
          if (categories.length > 0 && !categories.includes(data.category)) {
            continue
          }
          
          seenIds.add(doc.id)
          allPois.push({
            id: doc.id,
            lat: data.lat,
            lng: data.lng,
            category: data.category,
            confidence: data.confidence || 70,
            name: data.name,
            ...(lightweight ? {} : { rating: data.rating })
          })
        }
      }
      
      const truncated = allPois.length >= limit
      const elapsedMs = Date.now() - startTime
      
      logger.info('✅ POI search complete', {
        found: allPois.length,
        limit,
        truncated,
        elapsedMs,
        viewportKm: approxViewportKm.toFixed(1)
      })
      
      return {
        pois: allPois,
        total: allPois.length,
        truncated,
        lightweight
      }
    } catch (error) {
      logger.error('Viewport POI search failed', { error, bounds })
      return { pois: [], total: 0, truncated: false, lightweight: true }
    }
  }
  
  /**
   * Utility: chunk array into smaller arrays
   */
  private chunkArray<T>(arr: T[], size: number): T[][] {
    const chunks: T[][] = []
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size))
    }
    return chunks
  }

  /**
   * Generate all geohashes that cover a bounding box
   * Returns hashes in spiral order from center for uniform distribution
   */
  private getGeohashesForBounds(
    bounds: { north: number; south: number; east: number; west: number },
    precision: number
  ): string[] {
    const hashes: string[] = []
    const seen = new Set<string>()
    
    const latRange = bounds.north - bounds.south
    const lngRange = bounds.east - bounds.west
    
    // Grid size based on precision
    let gridSize: number
    if (precision <= 4) gridSize = 8
    else if (precision === 5) gridSize = 6
    else gridSize = 5
    
    const latStep = latRange / gridSize
    const lngStep = lngRange / gridSize
    
    // Generate in SPIRAL order from center for uniform distribution
    const centerLat = (bounds.north + bounds.south) / 2
    const centerLng = (bounds.east + bounds.west) / 2
    
    const addHash = (lat: number, lng: number) => {
      if (lat >= bounds.south && lat <= bounds.north && lng >= bounds.west && lng <= bounds.east) {
        const hash = geohash(lat, lng, precision)
        if (!seen.has(hash)) {
          seen.add(hash)
          hashes.push(hash)
        }
      }
    }
    
    // Start with center
    addHash(centerLat, centerLng)
    
    // Spiral outward
    for (let ring = 1; ring <= gridSize; ring++) {
      // Top and bottom edges
      for (let i = -ring; i <= ring; i++) {
        addHash(centerLat + ring * latStep, centerLng + i * lngStep)
        addHash(centerLat - ring * latStep, centerLng + i * lngStep)
      }
      // Left and right edges (excluding corners already done)
      for (let i = -ring + 1; i < ring; i++) {
        addHash(centerLat + i * latStep, centerLng - ring * lngStep)
        addHash(centerLat + i * latStep, centerLng + ring * lngStep)
      }
    }
    
    return hashes
  }

  /**
   * Get 8 neighboring geohash cells
      
      let query = this.db
        .collection('poi_database')
        .where(geohashField, 'in', queryHashes)
        .orderBy('confidence', 'desc')
        .limit(maxPois + 20)
      
      if (lightweight) {
        query = query.select('lat', 'lng', 'category', 'confidence', 'name', 'geohash4', 'geohash5', 'geohash6')
      }
      
      const snapshot = await query.get()
      
      // Cache by geohash and collect results
      const byGeohash = new Map<string, any[]>()
      for (const hash of queryHashes) {
        byGeohash.set(hash, [])
      }
      
      for (const doc of snapshot.docs) {
        const data = doc.data()
        const poi = {
          id: doc.id,
          lat: data.lat,
          lng: data.lng,
          category: data.category,
          confidence: data.confidence || 70,
          name: data.name,  // Always include name for UI
          ...(lightweight ? {} : { rating: data.rating })
        }
        
        // Cache
        const poiHash = data[geohashField]
        if (byGeohash.has(poiHash)) {
          byGeohash.get(poiHash)!.push(poi)
        }
        
        // Add to results if in bounds
        if (pois.length < maxPois &&
            poi.lat >= cellBounds.south && poi.lat <= cellBounds.north &&
            poi.lng >= cellBounds.west && poi.lng <= cellBounds.east) {
          if (categories.length === 0 || categories.includes(poi.category)) {
            pois.push(poi)
          }
        }
      }
      
      // Save to cache
      for (const [hash, hashPois] of byGeohash) {
        if (hashPois.length > 0) {
          setGeohashCache(precision, hash, hashPois)
        }
      }
    }
    
    return pois
  }
  
  /**
   * Fetch POIs for a single grid cell with limit (LEGACY - kept for compatibility)
   */
  private async fetchPOIsForCell(
    cellBounds: { north: number; south: number; east: number; west: number },
    precision: 4 | 5 | 6,
    categories: string[],
    maxPois: number,
    lightweight: boolean
  ): Promise<any[]> {
    const geohashes = this.getGeohashesForBounds(cellBounds, precision)
    if (geohashes.length === 0) return []
    
    // Limit geohashes per cell
    const limitedHashes = geohashes.slice(0, 30)
    
    const pois: any[] = []
    const geohashField = `geohash${precision}` as const
    
    // Separate cached vs uncached geohashes
    const uncachedHashes: string[] = []
    
    for (const hash of limitedHashes) {
      const cached = getFromGeohashCache(precision, hash)
      if (cached) {
        // Filter cached POIs by bounds and add to results
        for (const poi of cached) {
          if (pois.length >= maxPois) break
          if (
            poi.lat >= cellBounds.south &&
            poi.lat <= cellBounds.north &&
            poi.lng >= cellBounds.west &&
            poi.lng <= cellBounds.east
          ) {
            // Category filter
            if (categories.length === 0 || categories.includes(poi.category)) {
              pois.push(poi)
            }
          }
        }
      } else {
        uncachedHashes.push(hash)
      }
    }
    
    // If we have enough from cache, return early
    if (pois.length >= maxPois || uncachedHashes.length === 0) {
      return pois.slice(0, maxPois)
    }
    
    // Query uncached geohashes from Firestore
    const chunks = this.chunkArray(uncachedHashes, 10)
    
    for (const chunk of chunks) {
      if (pois.length >= maxPois) break
      
      // Query WITH orderBy to use composite index (geohashX + confidence)
      // This makes the query MUCH faster with large datasets
      let query: any = this.db
        .collection('poi_database')
        .where(geohashField, 'in', chunk)
        .orderBy('confidence', 'desc')  // Use index!
        .limit(200)  // Reasonable limit per chunk for caching
      
      if (lightweight) {
        query = query.select('lat', 'lng', 'category', 'confidence', 'geohash4', 'geohash5', 'geohash6', 'geohash7', 'geohash8')
      }
      
      const snapshot = await query.get()
      
      // Group results by geohash for caching
      const byGeohash = new Map<string, any[]>()
      for (const hash of chunk) {
        byGeohash.set(hash, [])
      }
      
      for (const doc of snapshot.docs) {
        const data = doc.data()
        const poi: any = {
          id: doc.id,
          lat: data.lat,
          lng: data.lng,
          category: data.category,
          confidence: data.confidence || 70,
          geohash4: data.geohash4,
          geohash5: data.geohash5,
          geohash6: data.geohash6,
          geohash7: data.geohash7
        }
        
        if (!lightweight) {
          poi.name = data.name
          poi.rating = data.rating
        }
        
        // Add to geohash bucket for caching
        const poiHash = data[geohashField]
        if (byGeohash.has(poiHash)) {
          byGeohash.get(poiHash)!.push(poi)
        }
        
        // Add to results if in bounds and matches category
        if (pois.length < maxPois) {
          if (
            poi.lat >= cellBounds.south &&
            poi.lat <= cellBounds.north &&
            poi.lng >= cellBounds.west &&
            poi.lng <= cellBounds.east
          ) {
            if (categories.length === 0 || categories.includes(poi.category)) {
              pois.push(poi)
            }
          }
        }
      }
      
      // Cache all geohashes (even empty ones)
      for (const [hash, hashPois] of byGeohash) {
        setGeohashCache(precision, hash, hashPois)
      }
    }
    
    return pois.slice(0, maxPois)
  }
  
  /**
   * Query geohashes using chunked 'in' queries (Firestore limit: 10 per query)
   * Uses select() to only fetch required fields in lightweight mode (save bandwidth)
   */
  private async queryGeohashesChunked(
    geohashes: string[],
    precision: 4 | 5 | 6 | 7,
    categories: string[],
    lightweight: boolean = false
  ): Promise<FirebaseFirestore.QuerySnapshot<FirebaseFirestore.DocumentData>[]> {
    const geohashField = `geohash${precision}` as const
    
    // CRITICAL FIX: Query EACH geohash INDIVIDUALLY with small limit
    // This ensures uniform distribution across geography
    // Instead of: chunks of 10 geohashes with 1000 limit total
    // We do: each geohash with 50-100 limit, ensuring all areas represented
    const maxPerGeohash = precision <= 4 ? 100 : precision <= 5 ? 80 : precision <= 6 ? 60 : 40
    
    const queries = geohashes.map(hash => {
      let query: any = this.db
        .collection('poi_database')
        .where(geohashField, '==', hash)  // Single geohash, not 'in' array
      
      if (categories.length > 0 && categories.length <= 10) {
        query = query.where('categories', 'array-contains-any', categories)
      }
      
      // Lightweight mode: only fetch essential fields
      // CRITICAL: Include geohash fields for geographic distribution!
      if (lightweight) {
        query = query.select('lat', 'lng', 'category', 'confidence', 'geohash4', 'geohash5', 'geohash6', 'geohash7')
      }
      
      // CRITICAL: Small limit PER GEOHASH ensures uniform distribution
      return query.limit(maxPerGeohash).get()
    })
    
    // Execute in batches to avoid overwhelming Firestore
    const BATCH_SIZE = 50
    const results: FirebaseFirestore.QuerySnapshot<FirebaseFirestore.DocumentData>[] = []
    
    for (let i = 0; i < queries.length; i += BATCH_SIZE) {
      const batch = queries.slice(i, i + BATCH_SIZE)
      const batchResults = await Promise.all(batch)
      results.push(...batchResults)
    }
    
    return results
  }
  
  /**
   * Get appropriate POI limit based on zoom level
   * INVERTED LOGIC: Lower zoom = MORE POIs (for clustering visualization)
   * Higher zoom = Fewer POIs (for detailed street-level view)
   */
  private getLimitForZoom(zoom: number): number {
    // Zoomed IN = fewer POIs, more detail
    if (zoom >= 18) return 2000   // Very detailed street level
    if (zoom >= 16) return 3000   // Street level detail
    if (zoom >= 14) return 4000   // Neighborhood detail
    if (zoom >= 12) return 6000   // Neighborhood overview
    if (zoom >= 10) return 9000   // City level
    
    // Zoomed OUT = massive POIs for clustering visualization
    if (zoom >= 8) return 15000   // Region - start heavy clustering
    if (zoom >= 6) return 20000   // Country view - MASSIVE clustering ✅
    if (zoom >= 4) return 30000   // Large region - huge clustering
    return 60000                   // Continent - maximum clustering (all 60k!)
  }
  
  /**
   * Get geohash precision for clustering based on zoom level
   * CRITICAL: Must balance between:
   * - Too fine (many clusters, slower): geohash7
   * - Too coarse (few clusters, uneven): geohash4
   * 
   * Zoom 4-6:   geohash5 (~5km) - good spread without too many clusters
   * Zoom 7-10:  geohash6 (~600m) - more clusters, better distribution
   * Zoom 11-14: geohash6 (~600m) - same as zoom 10
   * Zoom 15+:   geohash7 (~76m) - very fine clustering
   */
  private getClusteringPrecisionForZoom(zoom: number): 4 | 5 | 6 | 7 {
    if (zoom <= 6) return 5      // Country: geohash5 (~5km)
    if (zoom <= 10) return 6     // Region: geohash6 (~600m)
    if (zoom <= 14) return 6     // City: geohash6 (~600m)
    return 7                     // Street: geohash7 (~76m)
  }

  /**
   * Extract or calculate geohash for a POI at given precision
   * Tries to use pre-calculated field, falls back to calculation
   */
  private getGeohashForPOI(poi: { lat: number; lng: number; [key: string]: any }, precision: 4 | 5 | 6 | 7): string {
    // Try to use pre-calculated geohash field
    const precisionField = `geohash${precision}` as keyof typeof poi
    if (poi[precisionField]) {
      return poi[precisionField] as string
    }
    
    // Try coarser pre-calculated fields that can be truncated
    if (precision <= 7 && poi.geohash7) {
      return (poi.geohash7 as string).slice(0, precision)
    }
    if (precision <= 6 && poi.geohash6) {
      return (poi.geohash6 as string).slice(0, precision)
    }
    if (precision <= 5 && poi.geohash5) {
      return (poi.geohash5 as string).slice(0, precision)
    }
    if (precision <= 4 && poi.geohash4) {
      return poi.geohash4 as string
    }
    
    // Fallback: calculate from lat/lng
    return geohash(poi.lat, poi.lng, precision)
  }
  
  /**
   * Get optimal geohash precision for viewport size
   * 
   * Strategy: Use finer precision for smaller viewports
   * Precision ranges (approximate):
   * - geohash8: ±4m (zoom 18+)
   * - geohash7: ±38m (zoom 15-17)
   * - geohash6: ±600m (zoom 13-14)
   * - geohash5: ±5km (zoom 10-12)
   * - geohash4: ±20km (zoom 6-9)
   */
  private getPrecisionForViewport(radiusKm: number): 4 | 5 | 6 {
    // Use appropriate precision based on viewport radius
    // CRITICAL: Never return 7 - most POIs don't have geohash7 indexed
    // Lower precision = coarser grid = more results but less locality
    
    if (radiusKm <= 10) return 6     // Very zoomed in: geohash6 (~600m)
    if (radiusKm <= 50) return 6     // Close: geohash6 (~600m)  
    if (radiusKm <= 150) return 5    // Medium: geohash5 (~5km)
    if (radiusKm <= 400) return 5    // Medium-far: geohash5 (~5km)
    return 4                         // Far/country: geohash4 (~20km)
  }
  
  /**
   * Select geohashes - now just takes first N since they're already in spiral order
   */
  private selectDistributedGeohashes(
    allHashes: string[],
    maxCount: number,
    bounds: { north: number; south: number; east: number; west: number },
    precision: number
  ): string[] {
    // Hashes are already in spiral order (center first), so just slice
    return allHashes.slice(0, maxCount)
  }

  /**
   * Fetch full POI details by ID
   * Used when user clicks on a POI marker to get full information
   */
  async getPOIDetails(poiId: string): Promise<{
    id: string
    name: string
    lat: number
    lng: number
    category: string
    description?: string
    rating?: number
    confidence: number
    tags?: string[]
    coverImage?: string
    verification?: {
      source: string
      confidence: number
    }
  } | null> {
    try {
      const doc = await this.db
        .collection('poi_database')
        .doc(poiId)
        .get()

      if (!doc.exists) {
        return null
      }

      const data = doc.data()
      if (!data) {
        return null
      }

      return {
        id: doc.id,
        name: data.name,
        lat: data.lat,
        lng: data.lng,
        category: data.category,
        description: data.description,
        rating: data.rating,
        confidence: data.confidence || 70,
        tags: data.tags,
        coverImage: data.coverImage,
        verification: data.verification
      }
    } catch (error) {
      logger.error('Failed to fetch POI details', { poiId, error })
      return null
    }
  }
}
